"use client";

import { useEffect, useState } from "react";
import axiosInstance from "@/lib/axiosConfig";
import { EscuelaCard } from "./EscuelaCard";
import { SkeletonCard } from "./SkeletonCard";
import { ErrorDisplay } from "./ErrorDisplay";
import { NoResultsDisplay } from "./NoResultsDisplay";
import { Escuela, CardSearchProps } from "./types";

export default function CardSearch({
  selectedType,
  searchData,
  loading: externalLoading,
}: CardSearchProps) {
  const [items, setItems] = useState<Escuela[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [skeletonCount, setSkeletonCount] = useState(6);

  const isEscuela = selectedType === "escuela";

  // Función simple para cargar los datos
  const fetchData = async () => {
    try {
      setLoading(true);

      // Configurar los parámetros de búsqueda
      const params: any = {};

      // Aplicar filtros si existen
      if (searchData.deporte && searchData.deporte !== 0) {
        params.deporte = searchData.deporte;
      }
      if (searchData.pais && searchData.pais !== 0) {
        params.pais = searchData.pais;
      }
      if (searchData.localidad && searchData.localidad !== 0) {
        params.localidad = searchData.localidad;
      }

      // Si no es escuela, agregar el tipo
      if (!isEscuela) {
        params.tipo = selectedType;
      }

      // Determinar el endpoint basado en el tipo seleccionado
      const endpoint = isEscuela ? "escuela" : "instructor/instructor";

      // Realizar la petición
      const response = await axiosInstance.get(endpoint, { params });

      // Actualizar el estado con los datos recibidos
      if (response.data && response.data.length > 0) {
        setItems(response.data);
        setSkeletonCount(Math.min(response.data.length, 6));
      } else {
        setItems([]);
      }

      setError(null);
    } catch (err: any) {
      console.error("Error al cargar datos:", err);

      // Manejar el error
      if (err.message && err.message.includes("non-threadsafe access to the session")) {
        setError(`El servidor está experimentando alta demanda. Por favor, intenta nuevamente en unos momentos.`);
      } else if (err.message && err.message.includes("tardado demasiado tiempo")) {
        setError(`La conexión está tardando demasiado. Por favor, verifica tu conexión a internet e intenta nuevamente.`);
      } else {
        setError(`Error al cargar ${isEscuela ? "las escuelas" : "los instructores"}`);
      }

      setItems([]);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedType, searchData]);

  // Función para manejar el reintento
  const handleRetry = () => {
    setRetrying(true);
    fetchData();
  };

  // Estilos CSS para animaciones
  useEffect(() => {
    if (!document.getElementById('custom-animations')) {
      const style = document.createElement('style');
      style.id = 'custom-animations';
      style.innerHTML = `
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite linear;
        }
        
        @keyframes softBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-soft-bounce {
          animation: softBounce 2s infinite ease-in-out;
        }
        
        .fade-in {
          opacity: 1;
          transition: opacity 0.5s ease-in-out;
        }
        
        .fade-out {
          opacity: 0;
          transition: opacity 0.5s ease-in-out;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const style = document.getElementById('custom-animations');
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  // Determinar la clase de la cuadrícula según la cantidad de elementos
  const getGridClass = () => {
    const length = items.length;
    if (length === 1) return "2xl:grid-cols-1";
    if (length < 3) return "md:grid-cols-2 2xl:grid-cols-2";
    return "grid-cols-1 md:grid-cols-2 2xl:grid-cols-3";
  };

  // Renderizar los esqueletos de carga
  const renderSkeletons = () => {
    return Array(skeletonCount).fill(0).map((_, index: number) => (
      <SkeletonCard key={`skeleton-card-${index}`} index={index} />
    ));
  };

  // Renderizar el contenido según el estado
  const renderContent = () => {
    if (loading || externalLoading) {
      return (
        <div id="search" className="max-w-4xl 2xl:max-w-[1300px] min-h-[50rem] pb-[10rem] mx-auto mt-10 grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
          {renderSkeletons()}
        </div>
      );
    }

    if (error) {
      return (
        <div id="search">
          <ErrorDisplay
            error={error}
            retrying={retrying}
            handleRetry={handleRetry}
          />
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div id="search">
          <NoResultsDisplay isEscuela={isEscuela} />
        </div>
      );
    }

    return (
      <div
        id="search"
        className={`max-w-4xl ${items.length < 3 ? "2xl:max-w-4xl" : "2xl:max-w-[1300px]"} min-h-[50rem] pb-[10rem] mx-auto mt-10 grid grid-cols-1 ${getGridClass()} gap-6`}
      >
        {items.map((item, index) => (
          <EscuelaCard
            key={`card-${item.usuarioId || index}-${selectedType}`}
            item={item}
            index={index}
            isEscuela={isEscuela}
            selectedType={selectedType}
          />
        ))}
      </div>
    );
  };

  return renderContent();
}
